import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { GameButton } from '@/components/ui/GameButton'
import { ArtImage } from '@/components/ui/ArtImage'
import { isTouchDevice } from '@/arcade'
import { characterArt, TILE_ART } from '@/game/art'
import type { PixelIconName } from '@/game/pixelIcons'

const EMBER_ART = characterArt('default_hero')

interface HowToPlayTutorialProps {
  open: boolean
  onClose: () => void
}

interface Slide {
  key: string
  icon: PixelIconName
  title: string
  body: string
  visual: (touch: boolean) => React.ReactNode
}

function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="h-9 w-9 rounded-[6px] shadow-emboss" style={{ background: color }} />
      <span className="text-[10px] font-semibold text-[var(--text-faint)]">{label}</span>
    </div>
  )
}

function EmberDot() {
  return EMBER_ART ? (
    <img src={EMBER_ART} alt="Ember" draggable={false} className="h-full w-full object-contain" />
  ) : (
    <span
      className="block h-full w-full rounded-full"
      style={{ background: 'radial-gradient(circle at 35% 30%, #ffe6b8, var(--ember) 60%)', boxShadow: '0 0 12px var(--ember)' }}
    />
  )
}

function SlideTrack({ dir, reducedMotion, wall }: { dir: 'up' | 'down' | 'left' | 'right'; reducedMotion: boolean; wall?: boolean }) {
  const dx = dir === 'left' ? -1 : dir === 'right' ? 1 : 0
  const dy = dir === 'up' ? -1 : dir === 'down' ? 1 : 0
  return (
    <div className="relative h-24 w-full max-w-[220px] overflow-hidden rounded-[var(--r-md)] bg-[var(--surface-sunken)] shadow-sunken">
      <div className="absolute inset-0 grid grid-cols-5 grid-rows-3">
        {Array.from({ length: 15 }, (_, i) => (
          <div key={i} className="border border-[var(--edge)]/10" />
        ))}
      </div>
      {wall ? (
        <span className="absolute right-2 bottom-3 h-8 w-8 overflow-hidden rounded-[4px] shadow-emboss">
          <ArtImage src={TILE_ART.wall} alt="Wall" fallbackIcon="levelSelect" fallbackColor="var(--text-dim)" className="h-full w-full" fit="cover" />
        </span>
      ) : (
        <span className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-[4px]" style={{ background: '#7de3ff55' }}>
          <PixelIcon name="home" size={12} />
        </span>
      )}
      <motion.div
        className="absolute left-3 bottom-3 h-8 w-8"
        style={{ filter: 'drop-shadow(0 0 10px var(--ember))' }}
        animate={
          reducedMotion
            ? undefined
            : wall
              ? { x: [0, 100, 100, 0], scaleX: [1, 1.35, 1, 1] }
              : { x: [0, dx * 116, 0], y: [0, dy * 56, 0], scaleX: [1, 1 + Math.abs(dx) * 0.4, 1], scaleY: [1, 1 + Math.abs(dy) * 0.4, 1] }
        }
        transition={{ duration: wall ? 2.6 : 2.2, repeat: Infinity, ease: 'easeInOut', times: wall ? [0, 0.45, 0.7, 1] : [0, 0.5, 1] }}
      >
        <EmberDot />
      </motion.div>
    </div>
  )
}

function MechanicRow({ color, icon, art, label, note }: { color: string; icon: PixelIconName; art?: string; label: string; note: string }) {
  return (
    <div className="flex items-center gap-3 rounded-[var(--r-sm)] bg-[var(--surface-sunken)] px-3 py-2 shadow-sunken">
      {art ? (
        <span className="h-9 w-9 shrink-0 overflow-hidden rounded-[6px] shadow-emboss">
          <ArtImage src={art} alt={label} fallbackIcon={icon} fallbackColor={color} className="h-full w-full" fit="cover" />
        </span>
      ) : (
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[6px]" style={{ background: color }}>
          <PixelIcon name={icon} size={15} />
        </span>
      )}
      <div className="min-w-0 text-left">
        <p className="text-sm font-bold leading-tight">{label}</p>
        <p className="text-xs text-[var(--text-faint)] leading-tight">{note}</p>
      </div>
    </div>
  )
}

export function HowToPlayTutorial({ open, onClose }: HowToPlayTutorialProps) {
  const touch = isTouchDevice()
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  const [index, setIndex] = useState(0)
  const [dir, setDir] = useState(1)

  const slides = useMemo<Slide[]>(
    () => [
      {
        key: 'goal',
        icon: 'home',
        title: 'Guide Ember home',
        body: 'Slide Ember into the glowing window. Fewer slides, more stars.',
        visual: () => <SlideTrack dir="right" reducedMotion={!!reducedMotion} />,
      },
      {
        key: 'momentum',
        icon: 'speed',
        title: 'Momentum sliding',
        body: 'Ember never stops on its own. It slides until it hits something.',
        visual: () => <SlideTrack dir="right" wall reducedMotion={!!reducedMotion} />,
      },
      {
        key: 'controls',
        icon: touch ? 'mobile' : 'gamepad',
        title: 'Controls',
        body: touch
          ? 'Swipe the board or use the corner joystick. Tap powerups to fire.'
          : 'Arrows or WASD to slide. Number keys fire powerups. Esc pauses.',
        visual: (t) => (
          <div className="flex items-center gap-4">
            {t ? (
              <div className="grid grid-cols-3 gap-1">
                {['', 'up', '', 'left', '', 'right', '', 'down', ''].map((d, i) =>
                  d ? (
                    <span key={i} className="grid h-8 w-8 place-items-center rounded-[6px] bg-[var(--surface-raised)] shadow-emboss text-[var(--ember)]">
                      <PixelIcon
                        name="arrow-left"
                        size={14}
                        className={d === 'up' ? 'rotate-90' : d === 'right' ? 'rotate-180' : d === 'down' ? '-rotate-90' : ''}
                      />
                    </span>
                  ) : (
                    <span key={i} className="h-8 w-8" />
                  ),
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <span className="grid h-8 w-8 place-items-center rounded-[6px] bg-[var(--surface-raised)] shadow-emboss text-xs font-black">W</span>
                <div className="flex gap-1">
                  {['A', 'S', 'D'].map((k) => (
                    <span key={k} className="grid h-8 w-8 place-items-center rounded-[6px] bg-[var(--surface-raised)] shadow-emboss text-xs font-black">
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <span className="text-[var(--text-faint)]">
              <PixelIcon name="arrow-left" size={22} className="rotate-180" />
            </span>
            <SlideTrack dir="right" reducedMotion={!!reducedMotion} />
          </div>
        ),
      },
      {
        key: 'stars',
        icon: 'star',
        title: 'Stars and score',
        body: 'Beat the slide and time targets to earn all three stars.',
        visual: () => (
          <div className="flex items-center gap-2">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="text-[var(--coin)]"
                initial={reducedMotion ? undefined : { scale: 0, rotate: -30 }}
                animate={reducedMotion ? undefined : { scale: 1, rotate: 0 }}
                transition={{ delay: i * 0.18, type: 'spring', stiffness: 300, damping: 14 }}
              >
                <PixelIcon name="star" size={34} />
              </motion.span>
            ))}
          </div>
        ),
      },
      {
        key: 'hazards',
        icon: 'fire',
        title: 'Watch the world',
        body: 'Some tiles are lethal, some change your slide. Learn them first.',
        visual: () => (
          <div className="flex flex-col gap-2 w-full max-w-[280px]">
            <MechanicRow color="#ff6b5e55" icon="fire" art={TILE_ART.lava} label="Lava & Water" note="Lethal. Sliding in ends the run." />
            <MechanicRow color="#9fe8ff55" icon="speed" art={TILE_ART.ice} label="Ice" note="No friction, you keep sliding." />
            <MechanicRow color="#c98f5a55" icon="construction" art={TILE_ART.breakable} label="Breakable" note="Shatters and stops you once." />
          </div>
        ),
      },
      {
        key: 'mechanics',
        icon: 'lock',
        title: 'Keys, doors & portals',
        body: 'Grab keys for doors. Teleports pair up. Switches open everything.',
        visual: () => (
          <div className="flex flex-col gap-2 w-full max-w-[280px]">
            <MechanicRow color="#e5c15c55" icon="lock" art={TILE_ART.door} label="Key & Door" note="Grab the key, slide through the door." />
            <MechanicRow color="#c86aff55" icon="infinity" art={TILE_ART.teleport} label="Teleport" note="Hop to the paired pad." />
            <MechanicRow color="#8fd15f55" icon="lightning" art={TILE_ART.switch} label="Switch" note="Opens every door at once." />
          </div>
        ),
      },
      {
        key: 'powerups',
        icon: 'lightning',
        title: 'Powerups & gear',
        body: touch
          ? 'Tap powerups to fire shields, hints, magnets and more.'
          : 'Number keys fire shields, hints, magnets and more.',
        visual: () => (
          <div className="flex flex-wrap justify-center gap-2 max-w-[280px]">
            {(['shield', 'hint', 'magnet', 'clock', 'life', 'star'] as PixelIconName[]).map((ic, i) => (
              <motion.span
                key={ic}
                className="grid h-11 w-11 place-items-center rounded-[var(--r-sm)] bg-[var(--surface-raised)] shadow-emboss"
                initial={reducedMotion ? undefined : { y: 10, opacity: 0 }}
                animate={reducedMotion ? undefined : { y: 0, opacity: 1 }}
                transition={{ delay: i * 0.08 }}
              >
                <PixelIcon name={ic} size={18} />
              </motion.span>
            ))}
          </div>
        ),
      },
      {
        key: 'ready',
        icon: 'play',
        title: 'You are ready',
        body: 'Reach the window in as few slides as you can. Good luck.',
        visual: () => (
          <div className="flex items-center gap-3">
            <Swatch color="var(--ember)" label="Ember" />
            <span className="text-[var(--text-faint)]">
              <PixelIcon name="arrow-left" size={22} className="rotate-180" />
            </span>
            <Swatch color="#7de3ff" label="Home" />
          </div>
        ),
      },
    ],
    [touch, reducedMotion],
  )

  useEffect(() => {
    if (open) {
      setIndex(0)
      setDir(1)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') prev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index])

  const last = index === slides.length - 1
  const slide = slides[index]

  function next() {
    if (last) {
      onClose()
      return
    }
    setDir(1)
    setIndex((i) => Math.min(slides.length - 1, i + 1))
  }
  function prev() {
    setDir(-1)
    setIndex((i) => Math.max(0, i - 1))
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 'var(--z-modal)' as unknown as number }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="How to play"
            initial={{ scale: 0.92, y: 18, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 10, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 360, damping: 30 }}
            className="relative flex w-full max-w-md flex-col rounded-[var(--r-xl)] bg-[var(--surface)] shadow-raised border border-[var(--edge)]/50"
          >
            <div className="flex items-center justify-between px-5 pt-4">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[var(--text-faint)]">
                <PixelIcon name="book" size={14} /> How to play
              </span>
              <button
                onClick={onClose}
                className="text-xs font-semibold text-[var(--text-faint)] transition hover:text-[var(--text)]"
              >
                {last ? 'Close' : 'Skip'}
              </button>
            </div>

            <div className="grid min-h-[168px] place-items-center px-6 py-4">
              <AnimatePresence mode="wait" custom={dir}>
                <motion.div
                  key={slide.key}
                  custom={dir}
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, x: dir * 40 }}
                  animate={reducedMotion ? { opacity: 1 } : { opacity: 1, x: 0 }}
                  exit={reducedMotion ? { opacity: 0 } : { opacity: 0, x: dir * -40 }}
                  transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
                  className="grid w-full place-items-center"
                >
                  {slide.visual(touch)}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="px-6 text-center">
              <div className="mb-1 flex items-center justify-center gap-2">
                <span className="text-[var(--ember)]">
                  <PixelIcon name={slide.icon} size={18} />
                </span>
                <h2 className="font-display text-lg font-black text-balance">{slide.title}</h2>
              </div>
              <p className="mx-auto max-w-[340px] text-sm leading-relaxed text-[var(--text-dim)] text-pretty">{slide.body}</p>
            </div>

            <div className="mt-4 mb-2 flex justify-center gap-1.5">
              {slides.map((s, i) => (
                <button
                  key={s.key}
                  aria-label={`Go to step ${i + 1}`}
                  onClick={() => {
                    setDir(i > index ? 1 : -1)
                    setIndex(i)
                  }}
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: i === index ? 20 : 8,
                    background: i === index ? 'var(--ember)' : 'var(--edge)',
                  }}
                />
              ))}
            </div>

            <div className="flex items-center gap-2 p-4 pt-2">
              <GameButton size="sm" variant="ghost" silent disabled={index === 0} onClick={prev} icon="arrow-left">
                Back
              </GameButton>
              <GameButton size="sm" fullWidth onClick={next} icon={last ? 'play' : 'continue'}>
                {last ? "Let's play" : 'Next'}
              </GameButton>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
